import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";
import { toast } from "sonner";

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const soldOut = product.available === false;

  const go = () => navigate(`/product/${product.slug}`);

  const variantForSize = (size) =>
    product.variants?.find((v) => v.size === size && v.available) ||
    product.variants?.find((v) => v.size === size);

  const quickAdd = (e, size) => {
    e.stopPropagation();
    const variant = variantForSize(size) || product.variants?.[0];
    if (!variant) { toast.error("Unavailable"); return; }
    addItem(product, variant, 1);
    toast.success(`${product.name} (${size}) added to bag`);
  };

  return (
    <div
      className="group flex flex-col gap-3 cursor-pointer animate-fade-up opacity-0"
      style={{ animationDelay: `${index * 60}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={go}
      data-testid={`product-card-${product.slug}`}
    >
      <div className="relative aspect-[4/5] bg-[#f4f4f2] overflow-hidden">
        {product.badge && (
          <span className={`absolute top-3 left-3 z-10 text-cream text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 ${soldOut ? "bg-gray-500" : "bg-ink"}`}>
            {product.badge}
          </span>
        )}
        {product.original_price && !soldOut && (
          <span className="absolute top-3 right-3 z-10 bg-sale text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1">
            Sale
          </span>
        )}
        <img
          src={hovered && product.image_alt ? product.image_alt : product.image}
          alt={product.name}
          className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${soldOut ? "opacity-70" : ""}`}
        />
        {soldOut ? (
          <div className="absolute inset-0 flex items-end justify-center pb-4 pointer-events-none">
            <span className="bg-ink/90 text-cream text-xs font-bold uppercase tracking-widest px-4 py-2">Sold Out</span>
          </div>
        ) : (
          <div className="absolute bottom-0 left-0 right-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 bg-white/95 backdrop-blur-sm p-3 flex flex-col gap-2 shadow-[0_-8px_30px_rgba(0,0,0,0.12)]">
            <span className="text-[10px] font-bold uppercase tracking-widest text-center text-ink">Quick Add</span>
            <div className="flex gap-1.5 justify-center flex-wrap">
              {product.sizes.map((s) => (
                <button
                  key={s}
                  onClick={(e) => quickAdd(e, s)}
                  className="min-w-9 h-9 px-2 flex items-center justify-center border border-gray-200 text-xs font-bold hover:bg-ink hover:text-cream hover:border-ink transition-all"
                  data-testid={`quick-add-${product.slug}-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-bold uppercase tracking-wide text-ink line-clamp-1">{product.name}</h3>
        <div className="flex gap-2 items-center">
          <span className={`font-bold text-sm ${product.original_price ? "text-sale" : "text-ink"}`}>{money(product.price)}</span>
          {product.original_price && (
            <span className="text-gray-400 line-through text-sm">{money(product.original_price)}</span>
          )}
        </div>
      </div>
    </div>
  );
};
